import React, { useEffect, useState } from 'react';
import { FMButton } from '@own/fds-react';
import './img-spc-maintenance-service-cost.scss';
import {
    ImgSpcFieldLabelsContentFragment,
    ImgSpcHeadingContentFragment,
    ImgSpcResultingContentFragment,
    ImgSpcVehicleImageJson,
    ImgSpcZaContentFragment,
} from '../../../../models/img-service-price-calculator/img-service-price-calculator';
import DynamicTableSPC, {
    DynamicTableSPCProps,
} from '../img-spc-dynamic-table/img-spc-dynamic-table';
import { useAnalytics } from '../../../../hooks/use-analytics';
import DateTimeUtil from '../../../utils/date-time-util/date-time-util';

export type ImgMaintenanceServiceCost = {
    dealerName?: string;
    derivativeName?: string;
    serviceInterval?: string;
};

interface MaintenanceServiceCostProps {
    details: ImgMaintenanceServiceCost;
    onBack: () => void;
    resultsLabel?: ImgSpcResultingContentFragment;
    imageLabel?: ImgSpcVehicleImageJson;
    imgSpcDerivativeData: DynamicTableSPCProps['imgSpcDerivativeData'];
    fieldsLabel?: ImgSpcFieldLabelsContentFragment;
    currencyLabel?: ImgSpcZaContentFragment | null;
    headersLabel?: ImgSpcHeadingContentFragment;
    zaLabel?: ImgSpcZaContentFragment;
    selectedCountry: string;
    fetchCountryCode: string;
    freeLaborCouponChecked: boolean;
    timingBeltChecked: boolean;
    iolmChecked: boolean;
    mechanicalPartsChecked: boolean;
    selectedMechanicalPartNumbers: string[];
    onlyIOLMChecked: boolean;
    oilChangeIOMLChecked: boolean;
    isInspectionDiscountApplicable: boolean;
}

const MaintenanceServiceDetailsDisplay: React.FC<MaintenanceServiceCostProps> = ({
    details,
    onBack,
    resultsLabel,
    imageLabel,
    imgSpcDerivativeData,
    fieldsLabel,
    currencyLabel,
    headersLabel,
    zaLabel,
    selectedCountry,
    fetchCountryCode,
    freeLaborCouponChecked,
    timingBeltChecked,
    iolmChecked,
    mechanicalPartsChecked,
    selectedMechanicalPartNumbers,
    onlyIOLMChecked,
    oilChangeIOMLChecked,
    isInspectionDiscountApplicable,
}) => {
    const { dealerName, derivativeName, serviceInterval } = details;
    const [fireEvents] = useAnalytics();

    const formatServiceInterval = (text: string) => {
        return text.replace(
            /\b(\d{4,})(?=\s*(?:Kms?|Miles?|Km|Mi|\s|$))/gi,
            match => {
                return parseInt(match, 10).toLocaleString('en-US');
            }
        );
    };

    const formattedServiceInterval = serviceInterval
        ? formatServiceInterval(serviceInterval)
        : 'Not selected';

    const getVehicleImage = () => {
        if (!imageLabel?.vehicleImageJson || !derivativeName) return null;
        const match = imageLabel.vehicleImageJson.find(
            vehicle =>
                vehicle.variantName === derivativeName ||
                derivativeName.includes(vehicle.nameplateName)
        );
        if (match?.imageURL) {
            const aemBaseUrl = process.env.REACT_APP_AEM_BASE_URL ?? '';
            return `${aemBaseUrl}${match.imageURL}`;
        }
        return null;
    };
    const vehicleImageUrl = getVehicleImage();

    const [countryCode, setCountryCode] = useState<string | null>(null);
    useEffect(() => {
        const fetchCode = async () => {
            const code: string = fetchCountryCode.toUpperCase();
            setCountryCode(code?.toUpperCase());
        };
        fetchCode();
    }, [fetchCountryCode]);

    const columnDefinitions = [
        { key: 'description', width: '200px', fixed: true },
        ...(countryCode === 'ZAF' ? [{ key: 'brand', width: '200px' }] : []),
        { key: 'unitUsed', width: '200px' },
        { key: 'totalCost', width: '200px' },
    ];

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior:
                'scrollBehavior' in document.documentElement.style
                    ? 'smooth'
                    : 'auto',
        });
    }, []);

    function getCurrentTimeForMarket(countryCode: string) {
        const timezoneMap: Record<string, string> = {
            ZAF: 'Africa/Johannesburg',
            PHL: 'Asia/Manila',
            THA: 'Asia/Bangkok',
            VNM: 'Asia/Ho_Chi_Minh',
        };
        const currentDate = new Date();
        const timeZone = timezoneMap[countryCode] || '';
        const timeOptions: Intl.DateTimeFormatOptions = {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone,
        };
        try {
            const currentTime = new Intl.DateTimeFormat(
                'en-US',
                timeOptions
            ).format(currentDate);
            return currentTime;
        } catch (error) {
            return '';
        }
    }

    function getPdfTimestamp() {
        const currentDate = new Date();
        const formattedDate = DateTimeUtil.toLocaleDateString(
            { day: '2-digit', month: 'long', year: 'numeric' },
            currentDate
        )
            .replaceAll(',', '')
            .replaceAll(' ', ' - ');
        const formattedTime = getCurrentTimeForMarket(countryCode ?? '');
        return `${formattedDate} ${formattedTime}`;
    }

    const generatePDF = (event: React.MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault();
        const head = document.head || document.getElementsByTagName('head')[0];
        const style = document.createElement('style');
        style.media = 'print';

        style.appendChild(
            document.createTextNode(`
            @page { 
                size: A4 Portrait !important; 
                margin: 10mm !important; 
            }
            .img-spc-print-button { 
                display: none !important; 
            }
            .scroll-top { 
                display: none !important; 
            }
        `)
        );
        head.appendChild(style);
        window.print();
        fireEvents(
            ['spc-pdf-download'],
            undefined,
            {
                fieldName: resultsLabel?.viewAndPrintPdf,
            },
            false
        );
    };
    return (
        <div className="selected-details-display">
            <div className="button-container">
                <FMButton
                    htmlButtonType="button"
                    label={resultsLabel?.backButtonCTALabel}
                    type="secondary"
                    chevron={'left'}
                    className="img-spc-back-button"
                    onClick={() => {
                        fireEvents(
                            ['spc-navigation-cta'],
                            undefined,
                            {
                                ctaType: resultsLabel?.backButtonCTALabel,
                            },
                            false
                        );
                        onBack();
                    }}
                />
            </div>
            <div className="service-container">
                <h3 className="service-cost-title">
                    {resultsLabel?.maintainanceServiceCost}
                </h3>
                <time
                    data-testid="maintenance-pdf-time-stamp"
                    className="maintenance-pdf-time-stamp"
                    dateTime={getPdfTimestamp()}
                >
                    {getPdfTimestamp()}
                </time>
                <div className="service-content">
                    <div className="service-details">
                        <div className="detail-group">
                            <p className="detail-heading">
                                {resultsLabel?.servicingFor}
                            </p>
                            <p className="detail-value">
                                {derivativeName ?? 'Not selected'}
                            </p>
                            <p className="detail-heading">
                                {resultsLabel?.atDealerLabel}
                            </p>
                            <p className="detail-value">
                                {dealerName ?? 'Not selected'}
                            </p>
                            {!onlyIOLMChecked && serviceInterval && (
                                <>
                                    <p className="detail-heading">
                                        {resultsLabel?.basedOntitle}
                                    </p>
                                    <p className="detail-value">
                                        {formattedServiceInterval}
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="service-image-container">
                        {vehicleImageUrl ? (
                            <img
                                src={vehicleImageUrl}
                                alt={vehicleImageUrl}
                                className="service-image"
                            />
                        ) : null}
                    </div>
                </div>
                <div>
                    <DynamicTableSPC
                        columnDefinitions={columnDefinitions}
                        imgSpcDerivativeData={imgSpcDerivativeData}
                        resultsLabel={resultsLabel}
                        fieldsLabel={fieldsLabel}
                        currencyLabel={currencyLabel}
                        headersLabel={headersLabel}
                        zaLabel={zaLabel}
                        selectedCountry={selectedCountry}
                        freeLaborCouponChecked={freeLaborCouponChecked}
                        timingBeltChecked={timingBeltChecked}
                        iolmChecked={iolmChecked}
                        mechanicalPartsChecked={mechanicalPartsChecked}
                        selectedMechanicalPartNumbers={
                            selectedMechanicalPartNumbers
                        }
                        onlyIOLMChecked={onlyIOLMChecked}
                        oilChangeIOMLChecked={oilChangeIOMLChecked}
                        isInspectionDiscountApplicable={
                            isInspectionDiscountApplicable
                        }
                    />
                </div>
                <div>
                    <FMButton
                        htmlButtonType="button"
                        label={resultsLabel?.viewAndPrintPdf}
                        type="secondary"
                        className="img-spc-print-button"
                        onClick={generatePDF}
                    />
                </div>
                {resultsLabel?.descriptionBelowMaintenanceServiceCost ? (
                    <p className="maintenancedescription">
                        {resultsLabel?.descriptionBelowMaintenanceServiceCost}
                    </p>
                ) : (
                    <></>
                )}
                <div className="group-buttons">
                    <FMButton
                        htmlButtonType="button"
                        label={resultsLabel?.bookAServiceCTALabel}
                        type="primary"
                        className="img-spc-service-button"
                        onClick={() => {
                            fireEvents(
                                ['spc-navigation-cta'],
                                undefined,
                                {
                                    ctaType: resultsLabel?.bookAServiceCTALabel,
                                },
                                false
                            );
                            if (resultsLabel?.bookAServiceCTAPath) {
                                window.open(
                                    resultsLabel.bookAServiceCTAPath,
                                    '_blank'
                                );
                            }
                        }}
                    />
                    <FMButton
                        htmlButtonType="button"
                        label={resultsLabel?.findADealerCTALabel}
                        type="secondary"
                        className="img-spc-dealer-button"
                        onClick={() => {
                            fireEvents(
                                ['spc-navigation-cta'],
                                undefined,
                                {
                                    ctaType: resultsLabel?.findADealerCTALabel,
                                },
                                false
                            );
                            if (resultsLabel?.findADealerPath) {
                                window.open(
                                    resultsLabel.findADealerPath,
                                    '_blank'
                                );
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default MaintenanceServiceDetailsDisplay;
